import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { neuLadenTaste } from '../../lib/platform';

// Auffangnetz um den Seiteninhalt.
//
// Vorher gab es keins: ein Fehler beim Rendern — oder eine Seite, deren
// Programmteil nach einem Deploy nicht mehr geladen werden konnte — hat den
// kompletten Baum abgeräumt. Übrig blieb eine weiße Fläche ohne jeden Hinweis,
// und das Telefon-Team hat geraten, dass „nichts lädt". Jetzt bleibt die
// Navigation stehen und der Inhaltsbereich sagt, was zu tun ist.
//
// Der `resetKey` (die aktuelle Adresse) sorgt dafür, dass ein Wechsel auf eine
// andere Seite den Fehlerzustand wieder aufhebt — sonst bliebe die Meldung bis
// zum nächsten Neuladen kleben, auch auf Seiten, die einwandfrei laufen.
interface Props { children: ReactNode; resetKey?: string }
interface State { error: Error | null; resetKey?: string }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) return { error: null, resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[Callcenter] Seitenfehler:', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Ein fehlgeschlagener Seiten-Download sieht anders aus als ein Programm-
    // fehler und braucht eine andere Ansage.
    const istLadefehler = /dynamically imported module|Importing a module script failed|Failed to fetch/i
      .test(error.message);

    return (
      <div style={box}>
        <AlertTriangle size={22} style={{ color: 'var(--ton-orange)', flex: 'none', marginTop: 2 }} />
        <div>
          <h2 style={{ margin: '0 0 6px', fontSize: 17 }}>
            {istLadefehler ? 'Diese Seite konnte nicht geladen werden' : 'Hier ist etwas schiefgelaufen'}
          </h2>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 14, maxWidth: 620 }}>
            {istLadefehler
              ? `Das passiert meistens direkt nach einem Update: Die geöffnete Seite verweist noch auf die alte Fassung. Einmal neu laden (${neuLadenTaste()}) holt die aktuelle.`
              : `Bitte die Seite einmal neu laden (${neuLadenTaste()}). Bleibt es dabei, gib bitte Bescheid – mit Uhrzeit und was du gerade gemacht hast.`}
          </p>
          <button onClick={() => window.location.reload()} style={btn}>
            <RotateCw size={14} /> Neu laden
          </button>
          <details style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            <summary style={{ cursor: 'pointer' }}>Technische Details</summary>
            <pre style={pre}>{error.message}</pre>
          </details>
        </div>
      </div>
    );
  }
}

const box: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'flex-start',
  margin: '24px auto', padding: 20, maxWidth: 760,
  background: 'var(--surface)', border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)',
};
const btn: React.CSSProperties = {
  padding: '7px 14px', borderRadius: 'var(--radius-sm)', border: 'none',
  background: 'var(--rot-dunkel)', color: '#fff', fontSize: 14, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const pre: React.CSSProperties = {
  whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6,
  background: 'var(--surface-elevated)', padding: 8, borderRadius: 6,
};
